import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { verificationAPI } from '@/lib/api';
import { FileText, Upload, Check, Building2, Calendar, Hash, Clock, AlertCircle, Mail, MapPin, Globe, Receipt, CheckCircle, Send, Search } from 'lucide-react';

// Comprehensive list of all countries
const ALL_COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia",
  "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
  "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria",
  "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad",
  "Chile", "China", "Colombia", "Comoros", "Congo (Democratic Republic)", "Congo (Republic)", "Cook Islands",
  "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica",
  "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia",
  "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "French Polynesia", "Gabon", "Gambia", "Georgia",
  "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti",
  "Honduras", "Hong Kong", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel",
  "Italy", "Ivory Coast", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait",
  "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania",
  "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands",
  "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco",
  "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Caledonia", "New Zealand",
  "Nicaragua", "Niger", "Nigeria", "Niue", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan",
  "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
  "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines",
  "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles",
  "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa",
  "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
  "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tokelau", "Tonga", "Trinidad and Tobago",
  "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom",
  "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen",
  "Zambia", "Zimbabwe"
];

const BusinessVerify = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const fileInputRef = useRef(null);
  const taxDocInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Business Details
    businessName: user.company_name || '',
    businessEmail: '',
    businessPhone: '',
    country: user.country || '',
    address: '',
    city: '',
    postalCode: '',
    
    // Registration Details
    documentType: '',
    registrationNumber: '',
    taxNumber: '',
    establishedDate: '',
    expireDate: ''
  });
  
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [taxDoc, setTaxDoc] = useState(null);
  const [taxDocPreview, setTaxDocPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  
  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!countrySearch) return ALL_COUNTRIES;
    const search = countrySearch.toLowerCase();
    return ALL_COUNTRIES.filter(c => c.toLowerCase().includes(search));
  }, [countrySearch]);

  const documentTypes = [
    { value: 'business_license', label: 'Business License' },
    { value: 'registration_certificate', label: 'Registration Certificate' },
    { value: 'incorporation_docs', label: 'Company Incorporation Documents' },
    { value: 'trade_license', label: 'Trade License' },
    { value: 'other', label: 'Other Official Document' },
  ];

  const handleFileChange = (e, type = 'main') => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(selectedFile.type)) {
        toast.error('Please upload a PDF, JPG, or PNG file');
        return;
      }

      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }

      if (type === 'tax') {
        setTaxDoc(selectedFile);
        if (selectedFile.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onloadend = () => setTaxDocPreview(reader.result);
          reader.readAsDataURL(selectedFile);
        } else {
          setTaxDocPreview(null);
        }
      } else {
        setFile(selectedFile);
        if (selectedFile.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onloadend = () => setPreview(reader.result);
          reader.readAsDataURL(selectedFile);
        } else {
          setPreview(null);
        }
      }
    }
  };

  const handleSendEmailCode = async () => {
    if (!formData.businessEmail) {
      toast.error('Please enter your business email');
      return;
    }
    
    setVerifyingEmail(true);
    try {
      // Call API to send verification code to business email
      await verificationAPI.sendBusinessEmailCode(formData.businessEmail);
      setEmailCodeSent(true);
      toast.success('Verification code sent to your business email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    if (!emailCode || emailCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    
    setVerifyingEmail(true);
    try {
      await verificationAPI.verifyBusinessEmailCode(formData.businessEmail, emailCode);
      setEmailVerified(true);
      toast.success('Business email verified!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.businessName) {
      toast.error('Please enter your business name');
      return;
    }
    if (!formData.businessEmail) {
      toast.error('Please enter your business email');
      return;
    }
    if (!emailVerified) {
      toast.error('Please verify your business email first');
      return;
    }
    if (!formData.country) {
      toast.error('Please select your country');
      return;
    }
    if (!formData.address) {
      toast.error('Please enter your business address');
      return;
    }
    if (!formData.documentType) {
      toast.error('Please select a document type');
      return;
    }
    if (!formData.registrationNumber) {
      toast.error('Please enter your registration number');
      return;
    }
    if (!formData.taxNumber) {
      toast.error('Please enter your tax number');
      return;
    }
    if (!formData.establishedDate) {
      toast.error('Please enter the established date');
      return;
    }
    // Document uploads are optional for business verification

    setLoading(true);

    try {
      // Convert files to base64 only if provided
      const fileToBase64 = (f) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });

      let mainDocBase64 = null;
      let taxDocBase64 = null;
      
      if (file) {
        mainDocBase64 = await fileToBase64(file);
      }
      if (taxDoc) {
        taxDocBase64 = await fileToBase64(taxDoc);
      }
      
      const response = await verificationAPI.verifyBusiness(
        formData.documentType, 
        mainDocBase64,
        formData.registrationNumber,
        formData.establishedDate,
        formData.expireDate,
        {
          businessName: formData.businessName,
          businessEmail: formData.businessEmail,
          businessPhone: formData.businessPhone,
          country: formData.country,
          address: formData.address,
          city: formData.city,
          postalCode: formData.postalCode,
          taxNumber: formData.taxNumber,
          taxDocument: taxDocBase64,
          emailVerified: emailVerified
        }
      );
      
      toast.success(response.data.message || 'Business verification submitted successfully!');
      if (response.data.note) {
        toast.info(response.data.note);
      }
      
      setTimeout(() => {
        navigate(user.role === 'buyer' ? '/buyer/dashboard' : '/seller/dashboard');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit verification');
    } finally {
      setLoading(false);
    }
  };

  // Check if user is business type
  if (user.business_type !== 'business') {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h1 className="text-2xl font-chivo font-bold text-slate-900 mb-2">
            Not Required
          </h1>
          <p className="text-slate-600 mb-6">
            Business verification is only for business accounts. Individual users should use face verification.
          </p>
          <Button onClick={() => navigate(user.role === 'buyer' ? '/buyer/dashboard' : '/seller/dashboard')}>
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface py-8 px-4">
      <Card className="w-full max-w-2xl mx-auto p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 data-testid="business-verify-heading" className="text-3xl font-chivo font-bold text-slate-900 mb-2">
            Business Verification
          </h1>
          <p className="text-slate-600">
            Complete your business details and upload verification documents
          </p>
        </div>

        <form data-testid="business-verify-form" onSubmit={handleSubmit} className="space-y-6">
          
          {/* Section 1: Business Information */}
          <div className="bg-slate-50 p-5 rounded-xl">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
              <Building2 className="w-5 h-5 mr-2 text-blue-600" />
              1. Business Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Business Name */}
              <div className="md:col-span-2">
                <Label htmlFor="businessName">Business Name *</Label>
                <Input
                  id="businessName"
                  data-testid="business-name-input"
                  placeholder="Your Company Pty Ltd"
                  value={formData.businessName}
                  onChange={(e) => setFormData({...formData, businessName: e.target.value})}
                />
              </div>

              {/* Business Email with Verification */}
              <div className="md:col-span-2">
                <Label htmlFor="businessEmail">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Business Email *
                </Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="businessEmail"
                    type="email"
                    data-testid="business-email-input"
                    placeholder="info@yourcompany.com"
                    value={formData.businessEmail}
                    onChange={(e) => setFormData({...formData, businessEmail: e.target.value})}
                    disabled={emailVerified}
                    className={emailVerified ? 'bg-green-50 border-green-300' : ''}
                  />
                  {!emailVerified && !emailCodeSent && (
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={handleSendEmailCode}
                      disabled={verifyingEmail || !formData.businessEmail}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Verify
                    </Button>
                  )}
                  {emailVerified && (
                    <div className="flex items-center text-green-600 px-3">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                  )}
                </div>
                
                {/* Email Verification Code Input */}
                {emailCodeSent && !emailVerified && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <Label className="text-sm text-blue-800">Enter verification code sent to {formData.businessEmail}</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        type="text"
                        placeholder="000000"
                        value={emailCode}
                        onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        maxLength={6}
                        className="text-center tracking-widest"
                      />
                      <Button 
                        type="button"
                        onClick={handleVerifyEmailCode}
                        disabled={verifyingEmail || emailCode.length !== 6}
                      >
                        {verifyingEmail ? 'Verifying...' : 'Confirm'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Business Phone */}
              <div>
                <Label htmlFor="businessPhone">Business Phone</Label>
                <Input
                  id="businessPhone"
                  type="tel"
                  data-testid="business-phone-input"
                  placeholder="+61 2 1234 5678"
                  value={formData.businessPhone}
                  onChange={(e) => setFormData({...formData, businessPhone: e.target.value})}
                />
              </div>

              {/* Country */}
              <div>
                <Label htmlFor="country">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Country *
                </Label>
                <Select 
                  value={formData.country} 
                  onValueChange={(v) => {
                    setFormData({...formData, country: v});
                    setCountrySearch('');
                  }}
                >
                  <SelectTrigger data-testid="country-select">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <div className="px-2 pb-2 sticky top-0 bg-white z-10">
                      <div className="flex items-center border rounded-md px-2">
                        <Search className="w-4 h-4 text-slate-400" />
                        <input 
                          type="text"
                          placeholder="Search country..."
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          className="w-full p-2 text-sm outline-none"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-sm text-slate-500">No country found</div>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <Label htmlFor="address">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Business Address *
                </Label>
                <Textarea
                  id="address"
                  data-testid="address-input"
                  placeholder="123 Business Street, Suite 100"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  rows={2}
                />
              </div>

              {/* City */}
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  data-testid="city-input"
                  placeholder="Sydney"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                />
              </div>

              {/* Postal Code */}
              <div>
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input
                  id="postalCode"
                  data-testid="postal-code-input"
                  placeholder="2000"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Section 2: Registration Details */}
          <div className="bg-slate-50 p-5 rounded-xl">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
              <Hash className="w-5 h-5 mr-2 text-blue-600" />
              2. Registration Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Document Type */}
              <div>
                <Label htmlFor="documentType">Document Type *</Label>
                <Select 
                  value={formData.documentType} 
                  onValueChange={(v) => setFormData({...formData, documentType: v})}
                >
                  <SelectTrigger data-testid="document-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Registration Number */}
              <div>
                <Label htmlFor="registrationNumber">
                  Registration Number *
                </Label>
                <Input
                  id="registrationNumber"
                  data-testid="registration-number-input"
                  placeholder="e.g., ABN 12 345 678 901"
                  value={formData.registrationNumber}
                  onChange={(e) => setFormData({...formData, registrationNumber: e.target.value})}
                />
              </div>

              {/* Tax Number */}
              <div>
                <Label htmlFor="taxNumber">
                  <Receipt className="w-4 h-4 inline mr-1" />
                  Tax Number (TIN/ABN/GST) *
                </Label>
                <Input
                  id="taxNumber"
                  data-testid="tax-number-input"
                  placeholder="e.g., TIN-123456789"
                  value={formData.taxNumber}
                  onChange={(e) => setFormData({...formData, taxNumber: e.target.value})}
                />
                <p className="text-xs text-slate-500 mt-1">Tax Identification Number as proof of business registration</p>
              </div>

              {/* Established Date */}
              <div>
                <Label htmlFor="establishedDate">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Established Date *
                </Label>
                <Input
                  id="establishedDate"
                  type="date"
                  data-testid="established-date-input"
                  value={formData.establishedDate}
                  onChange={(e) => setFormData({...formData, establishedDate: e.target.value})}
                />
              </div>

              {/* Expire Date */}
              <div>
                <Label htmlFor="expireDate">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Registration Expiry Date
                </Label>
                <Input
                  id="expireDate"
                  type="date"
                  data-testid="expire-date-input"
                  value={formData.expireDate}
                  onChange={(e) => setFormData({...formData, expireDate: e.target.value})}
                />
                <p className="text-xs text-slate-500 mt-1">If applicable</p>
              </div>
            </div>
          </div>

          {/* Section 3: Document Upload (Optional) */}
          <div className="bg-slate-50 p-5 rounded-xl">
            <h3 className="font-semibold text-slate-900 mb-2 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-600" />
              3. Document Upload (Optional)
            </h3>
            <p className="text-sm text-slate-500 mb-4">Supporting documents can strengthen your verification application</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Business Registration Document */}
              <div>
                <Label>Business Registration Document</Label>
                <div 
                  className={`mt-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                    file ? 'border-green-400 bg-green-50' : 'border-slate-300 hover:border-slate-400'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'main')}
                    className="hidden"
                    data-testid="file-input"
                  />
                  
                  {file ? (
                    <div className="space-y-2">
                      {preview ? (
                        <img src={preview} alt="Preview" className="max-h-24 mx-auto rounded" />
                      ) : (
                        <FileText className="w-10 h-10 text-green-600 mx-auto" />
                      )}
                      <p className="text-xs text-green-700 font-medium truncate">{file.name}</p>
                      <p className="text-xs text-green-600">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-10 h-10 text-slate-400 mx-auto" />
                      <p className="text-xs text-slate-600">Upload Registration Doc</p>
                      <p className="text-xs text-slate-500">PDF, JPG, PNG (max 10MB)</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Tax Registration Document */}
              <div>
                <Label>Tax Registration Document</Label>
                <div 
                  className={`mt-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                    taxDoc ? 'border-green-400 bg-green-50' : 'border-slate-300 hover:border-slate-400'
                  }`}
                  onClick={() => taxDocInputRef.current?.click()}
                >
                  <input
                    ref={taxDocInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'tax')}
                    className="hidden"
                    data-testid="tax-file-input"
                  />
                  
                  {taxDoc ? (
                    <div className="space-y-2">
                      {taxDocPreview ? (
                        <img src={taxDocPreview} alt="Tax Doc Preview" className="max-h-24 mx-auto rounded" />
                      ) : (
                        <Receipt className="w-10 h-10 text-green-600 mx-auto" />
                      )}
                      <p className="text-xs text-green-700 font-medium truncate">{taxDoc.name}</p>
                      <p className="text-xs text-green-600">{(taxDoc.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Receipt className="w-10 h-10 text-slate-400 mx-auto" />
                      <p className="text-xs text-slate-600">Upload Tax Certificate</p>
                      <p className="text-xs text-slate-500">PDF, JPG, PNG (max 10MB)</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Info Boxes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2 flex items-center">
                <FileText className="w-4 h-4 mr-2" />
                Accepted Documents
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Business License / Registration</li>
                <li>• Tax Registration Certificate</li>
                <li>• Company Incorporation Documents</li>
                <li>• Trade License</li>
              </ul>
            </div>

            <div className="bg-amber-50 p-4 rounded-lg">
              <h4 className="font-medium text-amber-800 mb-2 flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                Review Process
              </h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Documents reviewed by our team</li>
                <li>• Verification takes 24-48 hours</li>
                <li>• Email notification on completion</li>
              </ul>
            </div>
          </div>

          <Button
            data-testid="submit-business-verify-btn"
            type="submit"
            className="w-full h-12 text-base"
            disabled={loading || !emailVerified || !formData.documentType || !formData.registrationNumber || !formData.taxNumber || !formData.establishedDate || !formData.country || !formData.address}
          >
            {loading ? (
              'Submitting...'
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" />
                Submit Business Verification
              </>
            )}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <Button
            data-testid="skip-business-verification-btn"
            variant="ghost"
            size="sm"
            onClick={() => navigate(user.role === 'buyer' ? '/buyer/dashboard' : '/seller/dashboard')}
          >
            Skip for now
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default BusinessVerify;
